import { useEffect } from 'react';
import { useNotification, type Notification } from '../../contexts/NotificationContext';

const iconMap = {
  success: 'ri-check-line',
  error: 'ri-close-line',
  info: 'ri-information-line',
  warning: 'ri-alert-line',
};

const colorMap = {
  success: 'from-green-400 to-green-500',
  error: 'from-red-400 to-red-500',
  info: 'from-blue-400 to-blue-500',
  warning: 'from-yellow-400 to-yellow-500',
};

const bgColorMap = {
  success: 'bg-green-50 border-green-200',
  error: 'bg-red-50 border-red-200',
  info: 'bg-blue-50 border-blue-200',
  warning: 'bg-yellow-50 border-yellow-200',
};

const textColorMap = {
  success: 'text-green-800',
  error: 'text-red-800',
  info: 'text-blue-800',
  warning: 'text-yellow-800',
};

function ToastItem({ notification }: { notification: Notification }) {
  const { removeNotification } = useNotification();

  useEffect(() => {
    if (notification.duration && notification.duration > 0) {
      const timer = setTimeout(() => {
        removeNotification(notification.id);
      }, notification.duration);

      return () => clearTimeout(timer);
    }
  }, [notification, removeNotification]);

  return (
    <div
      className={`${bgColorMap[notification.type]} border rounded-xl p-4 shadow-lg animate-slideIn mb-3 min-w-[300px] max-w-md`}
    >
      <div className="flex items-start space-x-3">
        <div className={`w-8 h-8 bg-gradient-to-r ${colorMap[notification.type]} rounded-full flex items-center justify-center flex-shrink-0`}>
          <i className={`${iconMap[notification.type]} text-white text-lg`}></i>
        </div>
        <div className="flex-1">
          <p className={`text-sm font-medium ${textColorMap[notification.type]}`}>
            {notification.message}
          </p>
        </div>
        <button
          onClick={() => removeNotification(notification.id)}
          className={`${textColorMap[notification.type]} hover:opacity-70 transition-opacity flex-shrink-0`}
        >
          <i className="ri-close-line text-lg"></i>
        </button>
      </div>

      {notification.duration && notification.duration > 0 && (
        <div className="mt-2 h-1 bg-white/30 rounded-full overflow-hidden">
          <div
            className={`h-full bg-gradient-to-r ${colorMap[notification.type]} animate-progress`}
            style={{ animationDuration: `${notification.duration}ms` }}
          ></div>
        </div>
      )}
    </div>
  );
}

export default function ToastContainer() {
  const { notifications } = useNotification();

  if (notifications.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-[9999] pointer-events-none">
      <div className="pointer-events-auto">
        {notifications.map(notification => (
          <ToastItem key={notification.id} notification={notification} />
        ))}
      </div>

      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(100%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes progress {
          from {
            width: 100%;
          }
          to {
            width: 0%;
          }
        }

        .animate-slideIn {
          animation: slideIn 0.3s ease-out;
        }

        .animate-progress {
          animation: progress linear;
        }
      `}</style>
    </div>
  );
}

